import {
  Box,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  DialogTitle,
  AlertColor,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HeightRoundedIcon from '@mui/icons-material/HeightRounded';
import PageViewIcon from '@mui/icons-material/MenuBook';
import {
  FC,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useReducer,
} from 'react';
import MediumTypography from '../MediumTypography';
import './FilePreviewDialog.css';
import print from 'print-js';
import { extractFileExtension } from '../../../utils/fileUtils';
import '@cyntler/react-doc-viewer/dist/index.css';
import EnhancedDocViewer, {
  pdfReducer,
  initialPDFState,
  setZoomLevel,
  setCurrentPage,
  IPDFState,
} from './EnhancedDocViewer';
import SnackBarComponent from '../SnackBarComponent';

export interface FilePreviewProps {
  url: string;
  fileName?: string;
  onClose: () => void;
}

const FilePreviewDialog: FC<FilePreviewProps> = ({
  url,
  fileName,
  onClose,
}) => {
  const [open, setOpen] = useState(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);

  // Persist PDF state across remounts (dialog open/close cycles)
  const persistedPdfStateRef = useRef<IPDFState | null>(null);

  // PDF State Management using reducer
  const [pdfState, dispatch] = useReducer(
    pdfReducer,
    initialPDFState,
    () => persistedPdfStateRef.current || initialPDFState,
  );

  // Track component mounted state for cleanup
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Persist PDF state to ref on changes
  useEffect(() => {
    persistedPdfStateRef.current = pdfState;
  }, [pdfState]);

  // When the url changes, reset loading flag and PDF state so stale zoom/page
  // from a previous document does not carry over and controls are disabled
  // while the new document is loading.
  useEffect(() => {
    setIsDocumentLoading(true);
    dispatch({ type: 'SET_ZOOM_LEVEL', value: initialPDFState.zoomLevel });
    dispatch({ type: 'SET_CURRENT_PAGE', value: initialPDFState.currentPage });
    dispatch({ type: 'SET_NUM_PAGES', value: initialPDFState.numPages });
  }, [url, dispatch]);

  const formattedFileName = useMemo(
    () => fileName?.split('/').pop() || 'File',
    [fileName],
  );

  // Safe file extension extraction with error handling
  const getFileExtension = useCallback((fileUrl: string): string => {
    try {
      const extension = extractFileExtension(fileUrl);
      return extension ? extension.toLowerCase().replace('.', '') : '';
    } catch (error) {
      console.error('Error extracting file extension:', error);
      return '';
    }
  }, []);

  // Check if URL is a blob URL (handles both http and https)
  const isBlobUrl = useCallback((urlToCheck: string): boolean => {
    return urlToCheck.startsWith('blob:');
  }, []);

  // Memoize props to prevent EnhancedDocViewer re-renders
  const documents = useMemo(
    () => [
      {
        uri: url,
        fileName: fileName ?? '',
        fileType: isBlobUrl(url) ? 'pdf' : getFileExtension(url),
      },
    ],
    [url, fileName, isBlobUrl, getFileExtension],
  );

  const viewerStyle = useMemo(
    () => ({ width: '100%', height: '100%', overflow: 'auto' }),
    [],
  );

  const viewerConfig = useMemo(
    () => ({
      header: {
        disableHeader: true,
        disableFileName: true,
        retainURLParams: true,
      },
    }),
    [],
  );

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleRenderSuccess = useCallback(() => {
    setIsDocumentLoading(false);
  }, []);

  const handleRenderError = useCallback(() => {
    setIsDocumentLoading(false);
    if (isMountedRef.current) {
      setOpen(true);
      setToastrVariable('error');
      setToastrId('error.document.render');
      setToastrDefaultMessage('Failed to render document. Please try again.');
    }
  }, []);

  // PDF Control Handlers
  const goToPrevPage = useCallback(() => {
    dispatch(setCurrentPage(Math.max(pdfState.currentPage - 1, 1)));
  }, [pdfState.currentPage, dispatch]);

  const goToNextPage = useCallback(() => {
    dispatch(
      setCurrentPage(Math.min(pdfState.currentPage + 1, pdfState.numPages)),
    );
  }, [pdfState.currentPage, pdfState.numPages, dispatch]);

  const handleZoomIn = useCallback(() => {
    dispatch(setZoomLevel(pdfState.zoomLevel + pdfState.zoomJump));
  }, [pdfState.zoomLevel, pdfState.zoomJump, dispatch]);

  const handleZoomOut = useCallback(() => {
    dispatch(setZoomLevel(pdfState.zoomLevel - pdfState.zoomJump));
  }, [pdfState.zoomLevel, pdfState.zoomJump, dispatch]);

  const handleResetZoom = useCallback(() => {
    dispatch(setZoomLevel(pdfState.defaultZoomLevel));
  }, [dispatch]);

  const handlePdfDownload = useCallback(() => {
    if (!url) return;
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName?.split('/').pop() || 'document.pdf';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setOpen(true);
        setToastrVariable('error');
        setToastrId('ConsentForms.downloadErrorMsg');
        setToastrDefaultMessage('Failed to download');
      });
  }, [url, fileName]);

  const handlePrintDocument = useCallback(() => {
    if (!url) return;
    setIsPrinting(true);

    if (isBlobUrl(url)) {
      try {
        print({ printable: url, type: 'pdf' });
      } catch (error) {
        if (!isMountedRef.current) return;
        setOpen(true);
        setToastrVariable('error');
        setToastrId('error.print.failed');
        setToastrDefaultMessage('Failed to print PDF. Please try again.');
        console.error('Error printing document:', error);
      } finally {
        setIsPrinting(false);
      }
    } else {
      // Print button is only rendered when getFileExtension(url) === 'pdf',
      // so type: 'pdf' is always correct here.
      print({ printable: url, type: 'pdf' });
      setIsPrinting(false);
    }
  }, [url, isBlobUrl]);

  // Common icon style for all control buttons
  const iconButtonStyle = {
    color: 'var(--white)',
  };

  return (
    <>
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <Dialog
        className="imagePreviewModal"
        fullWidth
        open
        onClose={onClose}
        maxWidth={'lg'}
        PaperProps={{
          sx: { width: '100%', height: '90%' },
        }}
      >
        <DialogTitle className="paddingZero">
          <AppBar
            position="static"
            sx={{ backgroundColor: 'var(--thom-mono-gray4)' }}
            elevation={0}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={onClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <MediumTypography
                  sxProps={{ color: 'var(--white)', fontWeight: 'bold' }}
                  label={formattedFileName}
                  defaultlabel="File Name"
                />
              </Box>

              {/* PDF Controls - only show for PDF files */}
              {(isBlobUrl(url) || getFileExtension(url) === 'pdf') && (
                <>
                  {pdfState.paginated && pdfState.numPages > 1 && (
                    <Box className="flex__ mr-md">
                      <IconButton
                        onClick={goToPrevPage}
                        disabled={
                          pdfState.currentPage <= 1 || isDocumentLoading
                        }
                        size="small"
                        title="Previous Page"
                        sx={iconButtonStyle}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Box
                        className="mainText-md"
                        sx={{
                          color: '#fff',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: '60px',
                        }}
                      >
                        <MediumTypography
                          sxProps={{ flex: 1 }}
                          label={`${pdfState.currentPage} / ${pdfState.numPages}`}
                          defaultlabel="Page"
                        />
                      </Box>
                      <IconButton
                        onClick={goToNextPage}
                        disabled={
                          pdfState.currentPage >= pdfState.numPages ||
                          isDocumentLoading
                        }
                        size="small"
                        title="Next Page"
                        sx={iconButtonStyle}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}

                  <IconButton
                    onClick={handleZoomOut}
                    disabled={isDocumentLoading}
                    size="small"
                    title="Zoom Out"
                    sx={iconButtonStyle}
                  >
                    <ZoomOutIcon />
                  </IconButton>

                  <IconButton
                    onClick={handleZoomIn}
                    disabled={isDocumentLoading}
                    size="small"
                    title="Zoom In"
                    sx={iconButtonStyle}
                  >
                    <ZoomInIcon />
                  </IconButton>

                  <IconButton
                    onClick={handleResetZoom}
                    disabled={
                      pdfState.zoomLevel === pdfState.defaultZoomLevel ||
                      isDocumentLoading
                    }
                    size="small"
                    title="Reset Zoom"
                    sx={iconButtonStyle}
                  >
                    <RestartAltIcon />
                  </IconButton>

                  <IconButton
                    onClick={() =>
                      dispatch({
                        type: 'SET_PDF_PAGINATED',
                        value: !pdfState.paginated,
                      })
                    }
                    disabled={isDocumentLoading}
                    size="small"
                    title={
                      pdfState.paginated
                        ? 'Switch to Scroll View'
                        : 'Switch to Page View'
                    }
                    sx={iconButtonStyle}
                  >
                    {pdfState.paginated ? (
                      <HeightRoundedIcon />
                    ) : (
                      <PageViewIcon />
                    )}
                  </IconButton>
                </>
              )}

              <IconButton
                onClick={handlePdfDownload}
                size="small"
                title="Download"
                sx={iconButtonStyle}
                disabled={isDocumentLoading}
              >
                <DownloadIcon />
              </IconButton>

              {/* Print button for PDF */}
              {(isBlobUrl(url) || getFileExtension(url) === 'pdf') && (
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={handlePrintDocument}
                  aria-label="print"
                  disabled={isPrinting || isDocumentLoading}
                >
                  <PrintIcon />
                </IconButton>
              )}
            </Toolbar>
          </AppBar>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflow: 'hidden', height: '100%' }}>
          <EnhancedDocViewer
            documents={documents}
            style={viewerStyle}
            config={viewerConfig}
            pdfState={pdfState}
            dispatch={dispatch}
            onRenderSuccess={handleRenderSuccess}
            onRenderError={handleRenderError}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FilePreviewDialog;
